export type { GlossaryRow } from '@/states/slices/glossaryEntriesSlice';

/**
 * 無効行の理由
 */
export const INVALID_ROW_REASON = {
  /** 言語未入力 */
  NO_LANG: 'noLang',
  /** 単語未入力 */
  NO_CONTENT: 'noContent',
  /** 両方未入力 */
  BOTH: 'both',
} as const;

export type InvalidRowReason =
  (typeof INVALID_ROW_REASON)[keyof typeof INVALID_ROW_REASON];

/**
 * 警告の種別
 */
export const UPLOAD_WARNING_TYPE = {
  /** 重複行 */
  DUPLICATE: 'duplicate',
  /** 言語または単語が未入力の無効行 */
  MISSING_FIELD: 'missingField',
  /** 1言語あたり1000件上限を超えた行 */
  OVERFLOW: 'overflow',
  /** 非対応言語コードの行 */
  UNSUPPORTED_LANG: 'unsupportedLang',
  /** 読みの文字種が不正（日本語: ひらがな/カタカナのみ許可） */
  INVALID_SOUNDS_LIKE_JPN: 'invalidSoundsLikeJpn',
  /** 読みの文字種が不正（日本語以外: その言語の文字体系のみ許可） */
  INVALID_SOUNDS_LIKE_OTHER: 'invalidSoundsLikeOther',
  /** 単語（content）が Speechmatics の 1 エレメント 6 語上限を超過 */
  TOO_MANY_WORDS_CONTENT: 'tooManyWordsContent',
  /** 読み（sounds_like）の 1 件が Speechmatics の 1 エレメント 6 語上限を超過 */
  TOO_MANY_WORDS_SOUNDS_LIKE: 'tooManyWordsSoundsLike',
  /** 単語（content）内の 1 語が Speechmatics の 4000 文字上限を超過 */
  CONTENT_TOO_LONG: 'contentTooLong',
  /** 読み（sounds_like）内の 1 語が Speechmatics の 4000 文字上限を超過 */
  SOUNDS_LIKE_TOO_LONG: 'soundsLikeTooLong',
} as const;

export type UploadWarningType =
  (typeof UPLOAD_WARNING_TYPE)[keyof typeof UPLOAD_WARNING_TYPE];

/**
 * CSVパース時に検出された警告行の情報
 */
export type WarningRow = {
  /** CSVの行番号（1始まり） */
  rowNum: number;
  /** 言語コード */
  language: string;
  /** 単語 */
  content: string;
  /** 無効行の場合の理由。重複・未対応言語の場合は undefined。 */
  reason?: InvalidRowReason;
  /**
   * 問題のある読み文字列（下記警告の場合のみ設定）:
   * - INVALID_SOUNDS_LIKE_JPN / INVALID_SOUNDS_LIKE_OTHER: 文字種違反の読み
   * - TOO_MANY_WORDS_SOUNDS_LIKE: 語数超過の読み
   * - SOUNDS_LIKE_TOO_LONG: 文字数超過の読み
   * TOO_MANY_WORDS_CONTENT / CONTENT_TOO_LONG は content 側の問題のため設定しない。
   * 長い文字列の切り詰めは行わない。表示側（CSS ellipsis + title 属性）に委ねる。
   */
  soundsLike?: string;
};

/**
 * CSVアップロード時の警告グループ
 */
export type UploadWarning = {
  /** 警告の種別 */
  type: UploadWarningType;
  /** 該当行の一覧 */
  rows: WarningRow[];
};

/** 1言語あたりに登録できる用語集エントリの最大件数 */
export const MAX_ENTRIES_PER_LANGUAGE = 1000;

/** Speechmatics: additional_vocab の content / sounds_like 1 エレメントあたりの最大語数 */
export const SPEECHMATICS_MAX_WORDS_PER_ELEMENT = 6;

/** Speechmatics: content / sounds_like の 1 語あたりの最大文字数 */
export const SPEECHMATICS_MAX_WORD_LENGTH = 4000;

/** デフォルトのページあたり表示件数 */
export const PAGE_SIZE = 20;

/** ページあたり表示件数の選択肢 */
export const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
